import { useState } from "react";
import styles from "./styles.module.scss";
import { DateRange } from "react-date-range";
import { arSA } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// ---- TYPES ----

export type ToggleModeProps = {
  mode: "toggle";
  icon: string;
  isActive: boolean; // ← NEW
  onToggle: () => void;
};

interface MenuOption {
  label: string;
  value: string;
}

export type MenuModeProps = {
  mode: "menu";
  icon: string;

  // 🔴 NEW: split menu options
  mainOptions: MenuOption[];
  operationOptions: MenuOption[];

  selectedValues: Record<string, boolean>;

  onChange: (value: string, checked: boolean) => void;

  // 🔴 NEW: group select all handlers
  onSelectAllMain: (checked: boolean) => void;
  onSelectAllOperations: (checked: boolean) => void;
};

export interface CalendarRange {
  startDate: Date | null;
  endDate: Date | null;
}

export type CalendarModeProps = {
  mode: "calendar";
  icon: string;
  dateRange: CalendarRange;
  onDateChange: (range: CalendarRange) => void;
};

export type IconButtonProps =
  | ToggleModeProps
  | MenuModeProps
  | CalendarModeProps;

// ---- COMPONENT ----

const IconButton = (props: IconButtonProps) => {
  const { t } = useTranslation();
  const { icon } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  const isToggleActive = props.mode === "toggle" && props.isActive;

  // -----------------------------
  // CLICK HANDLER
  // -----------------------------
  const handleClick = () => {
    if (props.mode === "toggle") {
      props.onToggle();
      return;
    }

    if (props.mode === "menu" || props.mode === "calendar") {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.container} ${
          isToggleActive ? styles.activeToggle : ""
        }`}
        onClick={handleClick}>
        <img src={icon} alt="icon-button" />
      </div>

      {/* ----------------------------------------------------
          MENU MODE DROPDOWN
      ----------------------------------------------------- */}
      {props.mode === "menu" &&
        isOpen &&
        (() => {
          // ✅ props is now MenuModeProps
          const {
            mainOptions,
            operationOptions,
            selectedValues,
            onChange,
            onSelectAllMain,
            onSelectAllOperations,
          } = props;

          const isAllSelected = (options: MenuOption[]) =>
            options.length > 0 &&
            options.every((opt) => selectedValues[opt.value]);

          const MAX_ITEMS_PER_COLUMN = 10;

          const renderOptionsGrid = (options: MenuOption[]) => {
            const columns = Array.from(
              { length: Math.ceil(options.length / MAX_ITEMS_PER_COLUMN) },
              (_, colIndex) =>
                options.slice(
                  colIndex * MAX_ITEMS_PER_COLUMN,
                  colIndex * MAX_ITEMS_PER_COLUMN + MAX_ITEMS_PER_COLUMN
                )
            );

            return (
              <div className={styles.optionsGrid}>
                {columns.map((columnItems, colIndex) => (
                  <div key={colIndex} className={styles.optionsColumn}>
                    {columnItems.map((opt) => (
                      <label key={opt.value} className={styles.menuItem}>
                        <input
                          type="checkbox"
                          checked={!!selectedValues[opt.value]}
                          onChange={(e) =>
                            onChange(opt.value, e.target.checked)
                          }
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            );
          };

          return (
            <div className={styles.menu}>
              {/* MAIN OPTIONS */}
              <div className={styles.menuColumn}>
                <label className={styles.selectAll}>
                  <input
                    type="checkbox"
                    checked={isAllSelected(mainOptions)}
                    onChange={(e) => onSelectAllMain(e.target.checked)}
                  />
                  {t("lbl_basic_info_title")}
                </label>

                {renderOptionsGrid(mainOptions)}
              </div>

              <div className={styles.verticalDivider} />

              {/* OPERATION OPTIONS */}
              <div className={styles.menuColumn}>
                <label className={styles.selectAll}>
                  <input
                    type="checkbox"
                    checked={isAllSelected(operationOptions)}
                    onChange={(e) => onSelectAllOperations(e.target.checked)}
                  />
                  {t("lbl_operations")}
                </label>

                {renderOptionsGrid(operationOptions)}
              </div>
            </div>
          );
        })()}

      {/* ----------------------------------------------------
          CALENDAR MODE DATE PICKER
      ----------------------------------------------------- */}
      {props.mode === "calendar" && isOpen && (
        <div className={classNames(styles.calendarWrapper)}>
          <DateRange
            ranges={[
              {
                key: "selection",
                startDate: hasUserSelected
                  ? props.dateRange.startDate ?? undefined
                  : new Date(),
                endDate: hasUserSelected
                  ? props.dateRange.endDate ?? undefined
                  : new Date(),
              },
            ]}
            onChange={(ranges) => {
              const selection = ranges.selection;

              setHasUserSelected(true);

              props.onDateChange({
                startDate: selection.startDate ?? null,
                endDate: selection.endDate ?? null,
              });
            }}
            moveRangeOnFirstSelection={false}
            locale={arSA}
            editableDateInputs
          />
        </div>
      )}
    </div>
  );
};

export default IconButton;
